/**
 * Mindspace endpoints — graph, daily plan, quick bytes and practice.
 *
 * Owns the query-string builders for its three filtered feeds; they encode
 * this API's filter shape and have no other consumer.
 */
import type {
  ArchiveResponse,
  BookmarkResponse,
  CreatePracticeSessionPayload,
  DailyPlanSummary,
  HighlightsFeedFilters,
  HighlightsFeedResponse,
  KnowledgeGraphFilters,
  KnowledgeGraphResponse,
  PracticeSessionDto,
  PracticeStats,
  QueuePayload,
  ReviewResponse,
  ReviewSurface,
  SourceDocumentOption,
  SourceLessonOption,
  TagMatchMode,
  TagOption,
  UserTagsPatchResponse,
} from '@/types/api';
import { apiClient } from './client';

/**
 * Mindspace API endpoints
 */
// ============================================================================
// Mindspace - Phase 4/5 daily plan + queues + review
// ============================================================================

interface QueueQuery {
  domain?: string[];
  user?: string[];
  match?: TagMatchMode;
  limit?: number;
  questions_per_concept?: number;
  /** When true, restrict the queue to bookmarked (starred) concepts. */
  bookmarked?: boolean;
  /** Source-lesson allow-list. Restricts to concepts whose
   *  `source_lessons` intersects this list. */
  lessons?: string[];
  /** Explicit concept-id allow-list (re-practice). Returns exactly these
   *  concepts, bypassing the due-gate. */
  concept_ids?: string[];
  /** When true, include concepts that aren't currently due - "drill this
   *  collection" sessions (Custom / Recent lesson / Starred) practice every
   *  match, not just the due subset. Default (omitted) keeps it due-only. */
  include_not_due?: boolean;
  /** When true, skip the question cache and regenerate fresh quiz questions
   *  (used by "Practice again" so a repeat isn't the identical set). */
  refresh?: boolean;
}

function buildQueueQuery(q?: QueueQuery): string {
  if (!q) return '';
  const params = new URLSearchParams();
  if (q.domain?.length) params.set('domain', q.domain.join(','));
  if (q.user?.length) params.set('user', q.user.join(','));
  if (q.match) params.set('match', q.match);
  if (q.limit != null) params.set('limit', String(q.limit));
  if (q.questions_per_concept != null) {
    params.set('questions_per_concept', String(q.questions_per_concept));
  }
  if (q.bookmarked) params.set('bookmarked', 'true');
  if (q.include_not_due) params.set('include_not_due', 'true');
  if (q.refresh) params.set('refresh', 'true');
  for (const id of q.lessons ?? []) {
    params.append('lessons', id);
  }
  for (const id of q.concept_ids ?? []) {
    params.append('concept_ids', id);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

// ============================================================================
// KNOWLEDGE ARCHIVE GRAPH - Step 2 additions
// ============================================================================

function buildGraphQuery(filters?: KnowledgeGraphFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  for (const id of filters.source_lesson_ids ?? []) {
    params.append('lessons', id);
  }
  for (const id of filters.source_document_ids ?? []) {
    params.append('documents', id);
  }
  for (const t of filters.domain_tags ?? []) {
    params.append('domain', t);
  }
  for (const t of filters.user_tags ?? []) {
    params.append('user', t);
  }
  if (filters.match) params.set('match', filters.match);
  if (filters.time_axis) params.set('time_axis', filters.time_axis);
  if (filters.window) params.set('window', filters.window);
  if (filters.due_today) params.set('due_today', 'true');
  if (filters.include_orphaned === false) {
    params.set('include_orphaned', 'false');
  }
  if (filters.include_archived === true) {
    params.set('include_archived', 'true');
  }
  if (filters.archived_only === true) {
    params.set('archived_only', 'true');
  }
  if (filters.limit != null) params.set('limit', String(filters.limit));
  const s = params.toString();
  return s ? `?${s}` : '';
}

// ---- Practice session history ----

function buildHighlightsFeedQuery(filters?: HighlightsFeedFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  for (const id of filters.source_lesson_ids ?? []) {
    params.append('lessons', id);
  }
  for (const t of filters.domain_tags ?? []) {
    params.append('domain', t);
  }
  for (const t of filters.user_tags ?? []) {
    params.append('user', t);
  }
  if (filters.match) params.set('match', filters.match);
  if (filters.time_axis) params.set('time_axis', filters.time_axis);
  if (filters.window) params.set('window', filters.window);
  if (filters.due_today) params.set('due_today', 'true');
  if (filters.bookmarked) params.set('bookmarked', 'true');
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const mindspaceApi = {
  /**
   * Get mindspace graph for current user.
   * Accepts optional filter set; backend applies composite top-N ranking.
   */
  getGraph: (filters?: KnowledgeGraphFilters) =>
    apiClient.get<KnowledgeGraphResponse>(
      `/mindspace/graph${buildGraphQuery(filters)}`
    ),

  /**
   * Lessons that contributed concepts to the user's mindspace.
   * Populates the source-lesson filter dropdown.
   */
  getSourceLessons: () =>
    apiClient.get<SourceLessonOption[]>('/mindspace/source-lessons'),

  /**
   * Uploaded source documents whose bytes built any concept.
   * Populates the source-document filter dropdown.
   */
  getSourceDocuments: () =>
    apiClient.get<SourceDocumentOption[]>('/mindspace/source-documents'),

  /**
   * Stable list of every domain tag across the user's mindspace, with
   * per-tag concept counts. Used so the domain-tag filter option list
   * doesn't shift as other filters narrow the visible graph.
   */
  getDomainTagOptions: () =>
    apiClient.get<TagOption[]>('/mindspace/domain-tags'),

  /**
   * Stable list of every user-curated tag across the user's mindspace.
   * Same purpose as `getDomainTagOptions` for the user-tag section.
   */
  getUserTagOptions: () => apiClient.get<TagOption[]>('/mindspace/user-tags'),

  /**
   * Get last updated timestamp for mindspace
   */
  getLastUpdated: () => apiClient.get<any>('/mindspace/last-updated'),

  /**
   * Get concept details by ID
   */
  getConcept: (conceptId: string) =>
    apiClient.get<any>(`/mindspace/concepts/${conceptId}`),

  /**
   * Trigger manual concept extraction for current user
   */
  triggerExtraction: () => apiClient.post<any>('/mindspace/trigger-extraction'),

  /**
   * Delete a concept by ID
   */
  deleteConcept: (conceptId: string) =>
    apiClient.delete(`/mindspace/concepts/${conceptId}`),

  // -------- Phase 4/5 - daily plan + queues + review ---------------

  getDailyPlan: () => apiClient.get<DailyPlanSummary>('/mindspace/daily-plan'),

  /** Practice Arena header stats: durable learning streak + today's activity. */
  getPracticeStats: () =>
    apiClient.get<PracticeStats>('/mindspace/practice/stats'),

  refreshDailyPlan: () =>
    apiClient.post<{ success: boolean; message: string }>(
      '/mindspace/daily-plan/refresh'
    ),

  getQuickBytesQueue: (q?: QueueQuery) =>
    apiClient.get<QueuePayload>(
      `/mindspace/quick-bytes/queue${buildQueueQuery(q)}`
    ),

  /**
   * Cross-lesson highlights feed for the Quick Bytes microlearning surface.
   * Filters mirror the Knowledge Archive graph shape plus a `due_today`
   * intersect with the FSRS daily plan and a `bookmarked` intersect
   * with the user's saved concepts. Paginated via offset/limit.
   */
  getQuickBytesHighlights: (filters?: HighlightsFeedFilters) =>
    apiClient.get<HighlightsFeedResponse>(
      `/mindspace/quick-bytes/highlights${buildHighlightsFeedQuery(filters)}`
    ),

  addBookmark: (conceptId: string) =>
    apiClient.post<BookmarkResponse>(
      `/mindspace/concepts/${conceptId}/bookmark`
    ),

  removeBookmark: (conceptId: string) =>
    apiClient.delete<BookmarkResponse>(
      `/mindspace/concepts/${conceptId}/bookmark`
    ),

  /**
   * Pause a concept ("archive"): drop it from the revision pool (daily plan
   * + practice queue) and hide it from the default graph. Reversible via
   * `unarchiveConcept`; FSRS history is preserved.
   */
  archiveConcept: (conceptId: string) =>
    apiClient.post<ArchiveResponse>(`/mindspace/concepts/${conceptId}/archive`),

  /** Resume a paused concept: re-enter revision + the default graph. */
  unarchiveConcept: (conceptId: string) =>
    apiClient.delete<ArchiveResponse>(
      `/mindspace/concepts/${conceptId}/archive`
    ),

  getPracticeQueue: (q?: QueueQuery, options?: { signal?: AbortSignal }) =>
    apiClient.get<QueuePayload>(
      `/mindspace/practice/queue${buildQueueQuery(q)}`,
      options
    ),

  submitReview: (
    conceptId: string,
    body: {
      grade: 1 | 2 | 3 | 4;
      source: ReviewSurface;
      /** Idempotency token (UUID). When set, the server dedupes on
       *  `(user_id, concept_id, attempt_id)` within a 24h window - same
       *  attempt_id re-submitted returns the prior result without
       *  re-applying the FSRS grade. */
      attempt_id?: string;
      /** With it, `conceptId` may be the lesson's own concept id; a concept
       *  not in Mindspace yet comes back `pending` and is applied later. */
      lesson_id?: string;
    }
  ) =>
    apiClient.post<ReviewResponse & { deduped?: boolean; pending?: boolean }>(
      `/mindspace/concepts/${conceptId}/review`,
      body
    ),

  patchUserTags: (
    conceptId: string,
    body: { add?: string[]; remove?: string[] }
  ) =>
    apiClient.patch<UserTagsPatchResponse>(
      `/mindspace/concepts/${conceptId}/user-tags`,
      body
    ),

  /** Persist a finished or aborted practice session. The server stamps
   *  the `id` and trims the user's history to the most-recent 50. */
  createPracticeSession: (body: CreatePracticeSessionPayload) =>
    apiClient.post<PracticeSessionDto>('/mindspace/practice/sessions', body),

  /** Fetch the current user's recent practice sessions, most-recent first. */
  listPracticeSessions: (limit = 50) =>
    apiClient.get<PracticeSessionDto[]>(
      `/mindspace/practice/sessions?limit=${limit}`
    ),

  /** Drop every practice session for the current user. */
  clearPracticeSessions: () =>
    apiClient.delete<void>('/mindspace/practice/sessions'),
};
